import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLocation as useUserLocation } from "@/context/LocationContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Phone, Sparkles, Crosshair } from "lucide-react";
import { api } from "@/lib/api";

const OCCS = [
  { key: "service", label: "Service worker (plumber, electrician…)" },
  { key: "business_owner", label: "Shop / business owner" },
  { key: "job", label: "Looking for a job" },
  { key: "doctor", label: "Doctor / medical professional" },
  { key: "freelancer", label: "Freelancer / consultant" },
  { key: "student", label: "Student" },
  { key: "other", label: "Something else" },
];

export default function Onboarding() {
  const { user, setUser, loading } = useAuth();
  const { loc, detect, loading: locLoading } = useUserLocation();
  const [mobile, setMob] = useState("");
  const [profCat, setProfCat] = useState("service");
  const [occupation, setOccupation] = useState("");
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  if (loading) return null;                                   // BUG-A09: don't bounce a deep-link before auth resolves
  if (!user) return <Navigate to="/" replace />;

  const save = async () => {
    if (!/^\+?[0-9][0-9 -]{5,19}$/.test(mobile.trim())) { toast.error("Enter a valid mobile number (digits only)"); return; }
    setSaving(true);
    try {
      const r = await api.patch("/auth/profile", {
        name: user.name,
        mobile,
        profession_category: profCat,
        occupation,
        city: loc.city,
        lat: loc.lat,
        lng: loc.lng,
        business_category: profCat === "doctor" ? "doctor" : profCat === "business_owner" ? "store" : profCat === "job" ? "job" : "service",
        is_public: true,
      });
      setUser(r.data);
      toast.success("Welcome to find2near!");
      navigate("/account");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => x.msg).join(", ") : d || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-5 py-8">
      <div className="w-full max-w-lg bg-white border border-stone-200 rounded-3xl p-7 shadow-sm">
        <div className="w-14 h-14 rounded-2xl bg-[#FFF3EC] text-[#E05E36] grid place-items-center mb-4">
          <Sparkles size={26} />
        </div>
        <h1 className="heading text-2xl md:text-3xl font-extrabold text-stone-900">Join the neighbourhood</h1>
        <p className="text-stone-600 mt-1">Fill 3 quick details so we can auto-list you & help neighbours find you.</p>

        <div className="mt-6 space-y-4">
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-widest text-stone-500 flex items-center gap-1.5"><Phone size={12} /> Mobile</span>
            <Input value={mobile} onChange={e => setMob(e.target.value)} placeholder="+91 98…" className="mt-1.5 rounded-xl" autoFocus data-testid="onboarding-mobile" />
          </label>

          <label className="block">
            <span className="text-xs font-bold uppercase tracking-widest text-stone-500">I am a…</span>
            <Select value={profCat} onValueChange={setProfCat}>
              <SelectTrigger className="mt-1.5 rounded-xl" data-testid="onboarding-profession"><SelectValue /></SelectTrigger>
              <SelectContent>{OCCS.map(o => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </label>

          <label className="block">
            <span className="text-xs font-bold uppercase tracking-widest text-stone-500">Occupation / what you do</span>
            <Input value={occupation} onChange={e => setOccupation(e.target.value)} placeholder="e.g. Plumber, Doctor, Grocery shop owner" className="mt-1.5 rounded-xl" data-testid="onboarding-occupation" />
          </label>

          <button onClick={() => detect()} className="inline-flex items-center gap-1.5 text-xs font-bold text-[#E05E36]" data-testid="onboarding-locate">
            <Crosshair size={13} className={locLoading ? "animate-spin" : ""} /> {loc.source === "gps" ? `GPS on · ${loc.city}` : "Use my current location"}
          </button>
        </div>

        <div className="mt-6 flex gap-2">
          <Button onClick={save} disabled={saving} className="flex-1 rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="onboarding-save">
            {saving ? "Saving…" : "Save & continue"}
          </Button>
          <Button onClick={() => navigate("/dashboard")} variant="ghost" className="rounded-full" data-testid="onboarding-skip">Skip</Button>
        </div>
      </div>
    </div>
  );
}
